
import { Car, CarStatus, TestDriveRequest, CarRequest, StaffMember } from '../types';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  getDocs, 
  getDoc, 
  setDoc,
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy 
} from 'firebase/firestore';

/**
 * Dakar Auto Sales - Firebase Configuration
 */
const firebaseConfig = {
  apiKey: "AIzaSyB-tNqWUX6onkA1hwzgRzcam7WiERGVSW0",
  authDomain: "dakar1.firebaseapp.com",
  projectId: "dakar1",
  storageBucket: "dakar1.firebasestorage.app",
  messagingSenderId: "908496794258",
  appId: "1:908496794258:web:c2e085f281e5aff6d22c4b",
  measurementId: "G-WP1H12Y395"
};

const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
const db = getFirestore(app);
export const auth = getAuth(app);

class DataService {
  private getCollection(name: string) {
    return collection(db, name);
  }

  /**
   * Triggers an actual email via the Firebase "Trigger Email" extension.
   * Document is written to the 'mail' collection.
   */
  private async triggerEmailNotification(subject: string, htmlContent: string) {
    try {
      await addDoc(this.getCollection('mail'), {
        to: 'info@dakarautosales.com',
        message: {
          subject: `[WEBSITE INQUIRY] ${subject}`,
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; border: 1px solid #eee; padding: 20px;">
              <h2 style="color: #00853f; border-bottom: 2px solid #fdef42; padding-bottom: 10px;">Dakar Auto Sales - New Lead</h2>
              ${htmlContent}
              <hr style="border: none; border-top: 1px solid #eee; margin: 20px 0;">
              <p style="font-size: 10px; color: #999;">This inquiry was generated from the Dakar Auto Sales website and relayed via mail.dakarautosales.com.</p>
            </div>
          `
        },
        // Audit log for the SMTP details
        _smtp_config: "mail.dakarautosales.com:465"
      });
      console.log(`Email document created for: ${subject}`);
    } catch (error) {
      console.error("Failed to queue email:", error);
    }
  }

  // --- Cars ---
  async getCars(): Promise<Car[]> {
    try {
      const q = query(this.getCollection('cars'), orderBy('year', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Car));
    } catch (error) {
      console.error("Firestore Error (getCars):", error);
      return [];
    }
  }

  async getCarById(id: string): Promise<Car | undefined> {
    try {
      const docRef = doc(db, 'cars', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) return { id: docSnap.id, ...docSnap.data() } as Car;
      return undefined;
    } catch (error) {
      console.error("Firestore Error (getCarById):", error);
      return undefined;
    }
  }

  async addCar(car: Omit<Car, 'id'>): Promise<Car> {
    const docRef = await addDoc(this.getCollection('cars'), car);
    return { ...car, id: docRef.id } as Car;
  }

  async updateCar(id: string, updates: Partial<Car>): Promise<Car> {
    const docRef = doc(db, 'cars', id);
    await updateDoc(docRef, updates);
    const updatedSnap = await getDoc(docRef);
    return { id: updatedSnap.id, ...updatedSnap.data() } as Car;
  }

  async deleteCar(id: string): Promise<void> {
    const docRef = doc(db, 'cars', id);
    await deleteDoc(docRef);
  }

  // --- Test Drives ---
  async requestTestDrive(request: Omit<TestDriveRequest, 'id' | 'status'>): Promise<void> {
    const newRequest = { ...request, status: 'Pending', createdAt: new Date().toISOString() };
    await addDoc(this.getCollection('test_drives'), newRequest);
    
    await this.triggerEmailNotification(
      `Test Drive: ${request.carName}`,
      `
        <p><strong>Customer:</strong> ${request.customerName}</p>
        <p><strong>Email:</strong> ${request.email}</p>
        <p><strong>Phone:</strong> ${request.phone}</p>
        <p><strong>Vehicle:</strong> ${request.carName}</p>
        <p><strong>Requested:</strong> ${request.preferredDate} at ${request.preferredTime}</p>
      `
    );
  }

  async getTestDrives(): Promise<TestDriveRequest[]> {
    const q = query(this.getCollection('test_drives'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as TestDriveRequest));
  }

  // --- Car Sourcing Requests ---
  async submitCarRequest(request: Omit<CarRequest, 'id' | 'status'>): Promise<void> {
    const newReq = { ...request, status: 'New', createdAt: new Date().toISOString() };
    await addDoc(this.getCollection('car_requests'), newReq);
    
    await this.triggerEmailNotification(
      `Sourcing Request: ${request.make} ${request.model}`,
      `
        <h3>New Vehicle Request Details:</h3>
        <p><strong>Client:</strong> ${request.name}</p>
        <p><strong>Contact:</strong> ${request.email} / ${request.phone}</p>
        <p><strong>Searching for:</strong> ${request.yearRange} ${request.make} ${request.model}</p>
        <p><strong>Budget:</strong> ${request.priceRange}</p>
        <p><strong>Notes:</strong> ${request.message}</p>
      `
    );
  }

  async getCarRequests(): Promise<CarRequest[]> {
    const q = query(this.getCollection('car_requests'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CarRequest));
  }

  // --- Contact Inquiries ---
  async submitContactInquiry(inquiry: { name: string, email: string, subject: string, message: string }): Promise<void> {
    const newInquiry = { ...inquiry, createdAt: new Date().toISOString() };
    await addDoc(this.getCollection('contact_inquiries'), newInquiry);
    
    await this.triggerEmailNotification(
      `Contact Form: ${inquiry.subject}`,
      `
        <p><strong>From:</strong> ${inquiry.name} (${inquiry.email})</p>
        <p><strong>Subject:</strong> ${inquiry.subject}</p>
        <p><strong>Message:</strong></p>
        <div style="background: #f9f9f9; padding: 15px; border-radius: 5px;">${inquiry.message}</div>
      `
    );
  }

  // --- Staff Management ---
  async getStaffMembers(): Promise<StaffMember[]> {
    try {
      const q = query(this.getCollection('staff_members'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as StaffMember));
    } catch (error) {
      console.error("Firestore Error (getStaffMembers):", error);
      return [];
    }
  }

  async addStaffMember(staff: Omit<StaffMember, 'id'>): Promise<StaffMember> {
    const docRef = await addDoc(this.getCollection('staff_members'), staff);
    return { ...staff, id: docRef.id } as StaffMember;
  }

  async updateStaffMember(id: string, updates: Partial<StaffMember>): Promise<StaffMember> {
    const docRef = doc(db, 'staff_members', id);
    await updateDoc(docRef, updates);
    const updatedSnap = await getDoc(docRef);
    return { id: updatedSnap.id, ...updatedSnap.data() } as StaffMember;
  }

  async deleteStaffMember(id: string): Promise<void> {
    const docRef = doc(db, 'staff_members', id);
    await deleteDoc(docRef);
  }

  async getStaffMemberByEmail(email: string): Promise<StaffMember | undefined> {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const staffList = await this.getStaffMembers();
      return staffList.find(s => s.email.trim().toLowerCase() === normalizedEmail);
    } catch (error) {
      console.error("Firestore Error (getStaffMemberByEmail):", error);
      return undefined;
    }
  }

  // --- Site Settings ---
  async getSiteSettings(): Promise<{ underConstruction: boolean }> {
    try {
      const docRef = doc(db, 'settings', 'site_config');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return docSnap.data() as { underConstruction: boolean };
      }
      return { underConstruction: false };
    } catch (error) {
      console.error("Firestore Error (getSiteSettings):", error);
      return { underConstruction: false };
    }
  }

  async updateSiteSettings(settings: { underConstruction: boolean }): Promise<void> {
    const docRef = doc(db, 'settings', 'site_config');
    await setDoc(docRef, settings, { merge: true });
  }
}

export const dataService = new DataService();
